import { Checkbox, ScrollArea, Stack, Table, Title, Tooltip, useMantineTheme } from '@mantine/core'
import { randomId } from '@mantine/hooks'
import { IconDownload } from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next'

import { StakedItem } from '@/hooks/useStaking'
import { formatDays } from '@/utils/utils'

import Loading from '../Loading'
import TooltipText from '../TooltipText'
import classes from './WithdrawTable.module.css'

interface ActiveStakeValues {
  day: number
  l1: number
  stakingPower: number
  boost: number
  left: number
}

interface WithdrawTableProps {
  isLoading: boolean
  activeStakes: StakedItem[] | undefined
  getDayStart: (day: number) => string
  selectedRows: number[]
  setSelectedRows: (selectedRows: number[]) => void
}

const WithdrawTable: React.FC<WithdrawTableProps> = ({
  isLoading,
  activeStakes,
  getDayStart,
  selectedRows,
  setSelectedRows,
}) => {
  const theme = useMantineTheme()
  const { t } = useTranslation()

  const [withdrawOptions, setWithdrawOptions] = useState<ActiveStakeValues[]>([])
  const allChecked = useMemo(
    () => selectedRows.length === withdrawOptions.length && withdrawOptions.length > 0,
    [selectedRows, withdrawOptions]
  )
  const indeterminate = useMemo(
    () => selectedRows.length > 0 && !allChecked,
    [selectedRows, allChecked]
  )

  const activeStakeValues: ActiveStakeValues[] = useMemo(() => {
    if (activeStakes) {
      return activeStakes.map(stake => ({
        day: stake.day,
        l1: Number(stake.amount.fullValue),
        stakingPower: Number(stake.stake.fullValue),
        boost: Math.round(Number(stake.stake.fullValue) / Number(stake.amount.fullValue)),
        left: stake.left,
      }))
    }
    return []
  }, [activeStakes])

  useEffect(() => {
    setWithdrawOptions(activeStakeValues.filter(stake => stake.left < 0))
  }, [activeStakeValues, setSelectedRows])

  const handleSelectAll = useCallback(() => {
    if (!activeStakes) return
    if (allChecked) {
      setSelectedRows([])
    } else {
      setSelectedRows(activeStakes.filter(stake => stake.left < 0).map(stake => stake.day))
    }
  }, [activeStakes, allChecked])

  const rows = activeStakeValues.map(stake => (
    <Table.Tr
      key={randomId()}
      bg={selectedRows.includes(stake.day) ? theme.colors.background3[0] : undefined}
      style={{
        cursor: stake.left < 0 ? 'pointer' : 'not-allowed',
        opacity: stake.left < 0 ? 1 : 0.5,
      }}
    >
      <Table.Td className={classes.tableRow}>{`${stake.l1} L1`}</Table.Td>
      <Table.Td className={classes.tableRow}>{`x ${stake.boost}`}</Table.Td>
      <Table.Td className={classes.tableRow}>
        {stake.left < 0 ? (
          'Now'
        ) : (
          <TooltipText
            text={`In ~${formatDays(stake.left + 1)}`}
            tooltip={getDayStart(stake.day + 1)}
            position="right"
          />
        )}
      </Table.Td>
      <Table.Td>
        <Checkbox
          checked={selectedRows.includes(stake.day)}
          onChange={event =>
            setSelectedRows(
              event.currentTarget.checked
                ? [...selectedRows, stake.day]
                : selectedRows.filter(day => day !== stake.day)
            )
          }
          disabled={stake.left >= 0}
        />
      </Table.Td>
    </Table.Tr>
  ))

  const csvData = useMemo(() => {
    const headers = [['Staked', 'Multiplier', 'Staking Power', 'Withdrawable', 'Timestamp']]
    const data = activeStakeValues.map(stake => [
      `${stake.l1} L1`,
      `x ${stake.boost}`,
      `${stake.stakingPower}`,
      stake.left < 0 ? 'Now' : `In ~${formatDays(stake.left + 1)}`,
      getDayStart(stake.day + 1),
    ])
    return headers.concat(data)
  }, [activeStakeValues])

  return (
    <>
      <Stack
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingRight: '10px',
        }}
      >
        <Title order={4}>
          {t('components.staking.staking.modal.stake.stakeAmount', 'Stake Amount')}
        </Title>
        <CSVLink data={csvData} filename="Staking History.csv">
          <Tooltip
            label={t(
              'components.staking.staking.modal.stake.download',
              'Export Staking History CSV'
            )}
          >
            <IconDownload size="18px" />
          </Tooltip>
        </CSVLink>
      </Stack>
      <Table verticalSpacing={0} stickyHeader>
        <ScrollArea h={125}>
          <Table.Thead className={classes.tableHeader}>
            <Table.Tr>
              <Table.Th className={classes.tableHeader}>Staked</Table.Th>
              <Table.Th className={classes.tableHeader}>Multiplier</Table.Th>
              <Table.Th className={classes.tableHeader}>Withdrawable</Table.Th>
              <Table.Th>
                <Checkbox
                  indeterminate={indeterminate}
                  aria-label="Select row"
                  checked={allChecked}
                  onChange={handleSelectAll}
                />
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
          {isLoading && <Loading size="35px" style={{ margin: 'auto' }} />}
        </ScrollArea>
      </Table>
    </>
  )
}

export default WithdrawTable
